import { DataTableColumnHeader } from '@sweetspot/scramble-ds'
import { Check, Tag } from '@sweetspot/scramble-ds/atoms'
import { Payment } from '@sweetspot/shared/data-access/api-platform'
import { SubscriptionType } from '@sweetspot/shared/types'
import { priceToLocal } from '@sweetspot/shared/util/functions'
import { ColumnDef } from '@tanstack/react-table'
import { format } from 'date-fns'

export const columns = (
  t: (key: string) => string,
  handleSelect: (rows: Payment[]) => void
): ColumnDef<Payment>[] => {
  return [
    {
      id: 'select',
      header: ({ table }) => (
        <Check
          indeterminate={table.getIsSomePageRowsSelected()}
          checked={table.getIsAllPageRowsSelected()}
          onClick={(event) => event.stopPropagation()}
          onCheckedChange={(value: boolean) => {
            table.toggleAllPageRowsSelected(!!value)
            setTimeout(() => {
              handleSelect(table.getSelectedRowModel().rows.map((r) => r.original))
            }, 0)
          }}
          aria-label="Select all"
          className="translate-y-[2px] px-0"
        />
      ),
      cell: ({ row, table }) => (
        <Check
          checked={row.getIsSelected()}
          onClick={(event) => event.stopPropagation()}
          onCheckedChange={(value: boolean) => {
            row.toggleSelected(!!value)
            setTimeout(() => {
              handleSelect(table.getSelectedRowModel().rows.map((r) => r.original))
            }, 0)
          }}
          aria-label="Select row"
          className="translate-y-[2px] px-0"
        />
      ),
      enableSorting: false,
    },
    {
      accessorKey: 'player.first_name',
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      meta: { title: t('name') },
      cell: ({ row }) => {
        const player = row.original.player
        return player.first_name + ' ' + player.last_name
      },
    },
    {
      accessorKey: 'player.first_name',
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      meta: { title: t('email') },
      cell: ({ row }) => row.original.player.email,
      enableSorting: false,
    },
    {
      accessorKey: 'player.phone',
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      meta: { title: t('phone') },
      cell: ({ row }) => row.original.player.phone,
      enableSorting: false,
    },
    {
      accessorKey: 'membership',
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      meta: { title: t('membershipPassText') },
      cell: ({ row }) => {
        const membership = row.original.membership
        return (
          <div className="flex items-center gap-2">
            {membership.name}
            {membership.type === SubscriptionType.MEMBERSHIP ? (
              <Tag.Root variant="inverted" size="large" className="px-2 py-0.5 text-xs font-bold">
                <i className="fa-regular fa-badge-check text-lg leading-none" aria-hidden="true" />
                <span className="sr-only">{t('membership')}</span>
              </Tag.Root>
            ) : (
              <Tag.Root variant="inverted" size="large" className="py-0.5 text-xs font-bold">
                <i
                  className="fa-regular fa-ticket-perforated text-lg leading-none"
                  aria-hidden="true"
                />
                <span className="sr-only">{t('pass')}</span>
              </Tag.Root>
            )}
          </div>
        )
      },
      enableSorting: false,
    },
    {
      accessorKey: 'membership_card',
      header: () => <i className="fa fa-lock fa-solid" />,
      meta: { title: <i className="fa fa-lock fa-solid" /> },
      cell: ({ row }) => (
        <div>
          {row.original.membership_card?.is_locked ? (
            <i className="fa fa-lock fa-solid" aria-hidden="true" />
          ) : (
            '-'
          )}
        </div>
      ),
      enableSorting: false,
    },
    {
      accessorKey: 'installment_loan.payment_date',
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      meta: { title: t('transactions.header_date') },
      cell: ({ row }) => {
        const paymentDate = row.original.installment_loan.payment_date
        if (!paymentDate) return '-'
        return format(new Date(paymentDate), 'P')
      },
    },
    {
      accessorKey: 'installment_loan.term',
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      meta: { title: t('transactions.header_number') },
      cell: ({ row }) => {
        const installmentLoan = row.original.installment_loan
        const month = installmentLoan.month
        const term = installmentLoan.term
        if (month === null || term === null) return '-'
        return `${month}/${term}`
      },
    },
    {
      accessorKey: 'retry.attempts',
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      meta: { title: t('transactions.header_retries') },
      cell: ({ row }) => {
        const retry = row.original.retry
        return `${retry.attempts}/${retry.max_attempts}`
      },
    },
    {
      accessorKey: 'payment_provider_refusal_reason',
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      meta: { title: t('membership.settings.reason') },
      cell: ({ row }) => row.getValue('payment_provider_refusal_reason'),
      enableSorting: false,
    },
    {
      accessorKey: 'amount.value',
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      meta: { title: t('words.amount') },
      cell: ({ row }) => priceToLocal(row.original.amount.value, row.original.amount.currency),
    },
    {
      accessorKey: 'contract_id',
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      meta: { title: t('transactions.header_contractId') },
      cell: ({ row }) => row.getValue('contract_id'),
      enableSorting: false,
    },
    {
      accessorKey: 'uuid',
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      meta: { title: t('transactions.header_id') },
      cell: ({ row }) => row.getValue('uuid'),
      enableSorting: false,
    },
  ]
}
